/**
 * The REVY evidence contract, hand-mirrored from `domain/evidence/models.py` (Task #962,
 * Story #926, Epic #925). There is no OpenAPI->TS codegen in this repo.
 *
 * Field names are camelCase per the house boundary convention
 * (`services/verificationService.mappers.ts`), but every enum VALUE is the wire string
 * verbatim so a locale key can be built from one: `t(`evidence.status.${status}`)`.
 *
 * The enums are a public cross-Epic surface (`domain/evidence/models.py:7-8`): adding a
 * member here without its locale entry is caught by the i18n key-resolution test, and
 * `satisfies Record<...>` maps make an untranslated member a typecheck failure.
 */

/** `domain/evidence/models.py:30-41` — what produced a finding. */
export const GENERATOR_TYPES = ['retrieval', 'rule', 'geometry', 'model'] as const;
export type GeneratorType = typeof GENERATOR_TYPES[number];

/**
 * `domain/evidence/models.py:44-58`. `unverified` ("not checked yet") is deliberately not
 * `missing` ("sought and absent") — collapsing them shows an unchecked claim as an
 * unsupported one (invariant 4).
 */
export const VERIFICATION_STATUSES = [
  'unverified', 'verified', 'missing', 'stale', 'unsupported', 'conflict',
] as const;
export type VerificationStatus = typeof VERIFICATION_STATUSES[number];

/** `domain/evidence/models.py:90-97`. There is no URL/web member — see #966 escalation Q1. */
export const EVIDENCE_KINDS = [
  'document_page', 'standard_clause', 'past_trouble', 'geometry_calculation', 'model_output',
] as const;
export type EvidenceKind = typeof EVIDENCE_KINDS[number];

/**
 * `domain/evidence/models.py` — #1195. Whether the viewer can open a source NOW, stated
 * by the backend next to the HISTORICAL `verificationStatus`; neither is derived from the
 * other. The client never infers this from the claim, the id shape or the locator.
 */
export const EVIDENCE_AVAILABILITIES = ['available', 'unavailable'] as const;
export type EvidenceAvailability = typeof EVIDENCE_AVAILABILITIES[number];

/** In order of least disclosure: a denied viewer learns nothing beyond the denial. */
export const AVAILABILITY_REASONS = [
  'permission_denied', 'source_deleted', 'unsupported_locator',
] as const;
export type AvailabilityReason = typeof AVAILABILITY_REASONS[number];

/** The finding-level combination rule, stated once by the backend. */
export const FINDING_EVIDENCE_AVAILABILITIES = [
  'no_evidence', 'available', 'partially_unavailable', 'unavailable',
] as const;
export type FindingEvidenceAvailability = typeof FINDING_EVIDENCE_AVAILABILITIES[number];

/** `domain/evidence/models.py:123-130` — the same four values the DRBFM domain uses. */
export const SEVERITIES = ['critical', 'high', 'medium', 'low'] as const;
export type Severity = typeof SEVERITIES[number];

/** `domain/evidence/models.py:61-70`. */
export const ANCHOR_TYPES = [
  'geometry', 'assembly', 'drawing', 'requirement', 'process', 'material', 'concern',
] as const;
export type AnchorType = typeof ANCHOR_TYPES[number];

/**
 * `domain/evidence/models.py:73-87`. `no_anchor` exists so that "not anchored" is a stated
 * value rather than an absence — the UI must never render it as blank space.
 */
export const ANCHOR_MATURITIES = [
  'no_anchor', 'candidate_anchor', 'manual_anchor', 'verified_anchor',
  'rejected_anchor', 'stale_anchor', 'superseded_anchor',
] as const;
export type AnchorMaturity = typeof ANCHOR_MATURITIES[number];

/** `domain/evidence/models.py:133-145`. */
export interface Provenance {
  generatorType: GeneratorType;
  generatorVersion: string;
  createdBy: string | null;
  createdAt: string;
}

/**
 * `domain/evidence/models.py:148-177`.
 *
 * `locator` keys stay snake_case: it is an opaque `Dict[str, Any]` on the wire, not a
 * modelled field, so the service-boundary mapper must not rewrite them. Camel-casing
 * `page_number` would break every jump silently.
 *
 * `sourceRevisionId === null` means "unversioned source", never "current".
 * Array position in `ReviewFinding.evidence` IS the citation order — there is no rank field.
 *
 * `contentHash` is nullable for the same reason `ReviewFinding.claim` is: #1198's read view
 * (`application/evidence/views.py`) withholds it from a viewer whose locator was withheld,
 * because it digests a payload containing that locator. Null means WITHHELD — never "no
 * hash"; the stored domain value is never null.
 *
 * `sourceId` is null only when it WAS that hash — the chat-citation fallback for a citation
 * without a document id — and the hash is withheld (#1198): a withheld digest must not travel
 * on under another name. Such a ref is never available.
 *
 * `availability` / `availabilityReason` are #1195's contract: the reason is null exactly
 * when the ref is available.
 */
export interface EvidenceRef {
  sourceId: string | null;
  sourceRevisionId: string | null;
  locator: Record<string, unknown>;
  contentHash: string | null;
  retrievedAt: string;
  evidenceKind: EvidenceKind;
  availability: EvidenceAvailability;
  availabilityReason: AvailabilityReason | null;
}

/** `domain/evidence/models.py:180-207`. */
export interface AnchorRef {
  targetType: AnchorType;
  targetId: string;
  locator: Record<string, unknown>;
  maturity: AnchorMaturity;
  createdBy: string | null;
  verifiedBy: string | null;
}

/**
 * `domain/evidence/models.py:210-236`.
 *
 * `claim` is required-but-nullable, not optional: #965's redactor sets it to null for a
 * viewer without source access, and `application/decision/services.py:178` sets it to null
 * when a source was deleted. A null claim therefore means "the support is withheld",
 * whatever `verificationStatus` still says.
 */
export interface ReviewFinding {
  id: string;
  tenantId: string;
  subjectRevisionId: string;
  claim: string | null;
  severity: Severity;
  confidence: number;
  verificationStatus: VerificationStatus;
  provenance: Provenance;
  evidence: EvidenceRef[];
  anchors: AnchorRef[];
  /** #1195: the combination rule over `evidence[].availability`, stated by the backend. */
  evidenceAvailability: FindingEvidenceAvailability;
}

/**
 * `application/evidence/legacy_adapters.py:125-134`. Enumerable so a fourth backend member
 * fails the i18n key-resolution test instead of silently rendering the unknown fallback.
 */
export const CONVERSION_REASON_CODES = [
  'missing_source_revision', 'unsupported_locator', 'unsupported_source_type',
] as const;
export type ConversionReasonCode = typeof CONVERSION_REASON_CODES[number];

/** One thing the legacy conversion could not carry — `application/evidence/legacy_adapters.py:137-142`. */
export interface LegacyConversionNote {
  /** A wire string: the backend may add a member this build has not been taught. */
  reasonCode: ConversionReasonCode | (string & {});
  detail: string;
  legacyLocator: Record<string, unknown>;
}

/** `application/evidence/legacy_adapters.py:145-155`. */
export interface LegacyConversion {
  finding: ReviewFinding;
  notes: LegacyConversionNote[];
}
